/**
 * Runtime configuration.
 *
 * ONE IMAGE SERVES STAGING AND PROD. The container writes
 * `window.__MAFIAGO__` into /runtime-config.js at start
 * (deploy/40-runtime-config.sh), and index.html loads that file before the
 * bundle. In `vite dev` the placeholder in public/runtime-config.js leaves it
 * undefined and every field falls back to its VITE_* equivalent.
 *
 * Nothing here is a secret: the Mapbox token is a URL-restricted public `pk.`
 * token and the Firebase web config is public by design.
 */

export interface FirebaseWebConfig {
  apiKey: string
  authDomain: string
  projectId: string
  appId: string
}

export interface RuntimeConfig {
  authApiUrl: string
  buildingApiUrl: string
  npcApiUrl: string
  mapboxToken: string
  firebase: FirebaseWebConfig
  environment: string
  /** Runtime request for demo mode. Only honoured by a VITE_ENABLE_MOCK=1 build. */
  mock: boolean
}

/** What the container writes. Every field optional: a missing one falls back. */
export interface RawRuntimeConfig {
  authApiUrl?: string
  buildingApiUrl?: string
  npcApiUrl?: string
  mapboxToken?: string
  firebase?: Partial<FirebaseWebConfig>
  environment?: string
  mock?: boolean | string
}

declare global {
  interface Window {
    __MAFIAGO__?: RawRuntimeConfig
  }
}

const STAGING = {
  auth: 'https://auth-web-staging.mafiago.io',
  building: 'https://building-web-staging.mafiago.io',
  npc: 'https://npc-staging.mafiago.io',
}

function pick(...values: (string | undefined)[]): string {
  for (const v of values) {
    if (typeof v === 'string' && v.trim() !== '') return v.trim()
  }
  return ''
}

function stripSlash(url: string): string {
  return url.replace(/\/+$/, '')
}

function truthy(v: boolean | string | undefined): boolean {
  return v === true || v === '1' || v === 'true'
}

export function resolveRuntimeConfig(
  raw: RawRuntimeConfig | undefined,
  env: Record<string, string | undefined>,
): RuntimeConfig {
  const r = raw ?? {}
  const fb = r.firebase ?? {}
  return {
    authApiUrl: stripSlash(pick(r.authApiUrl, env.VITE_AUTH_API_URL, STAGING.auth)),
    buildingApiUrl: stripSlash(pick(r.buildingApiUrl, env.VITE_BUILDING_API_URL, STAGING.building)),
    npcApiUrl: stripSlash(pick(r.npcApiUrl, env.VITE_NPC_API_URL, STAGING.npc)),
    mapboxToken: pick(r.mapboxToken, env.VITE_MAPBOX_TOKEN),
    firebase: {
      apiKey: pick(fb.apiKey, env.VITE_FIREBASE_API_KEY),
      authDomain: pick(fb.authDomain, env.VITE_FIREBASE_AUTH_DOMAIN),
      projectId: pick(fb.projectId, env.VITE_FIREBASE_PROJECT_ID),
      appId: pick(fb.appId, env.VITE_FIREBASE_APP_ID),
    },
    environment: pick(r.environment, env.VITE_ENVIRONMENT, env.DEV ? 'development' : 'staging'),
    mock: truthy(r.mock) || truthy(env.VITE_MOCK_DEFAULT),
  }
}

let cached: RuntimeConfig | null = null

export function runtimeConfig(): RuntimeConfig {
  if (!cached) {
    const raw = typeof window !== 'undefined' ? window.__MAFIAGO__ : undefined
    cached = resolveRuntimeConfig(raw, import.meta.env as unknown as Record<string, string | undefined>)
  }
  return cached
}

export function firebaseConfigured(cfg: RuntimeConfig = runtimeConfig()): boolean {
  const f = cfg.firebase
  return Boolean(f.apiKey && f.authDomain && f.projectId && f.appId)
}

/** Compile-time: does this bundle contain the mock transport at all? */
export const MOCK_BUILD = import.meta.env.VITE_ENABLE_MOCK === '1'

const MOCK_SESSION_KEY = 'mafiago.mock'

/**
 * Demo mode: a VITE_ENABLE_MOCK=1 build, AND a runtime request (the runtime
 * flag, or `?mock=1`, remembered for the tab). A production bundle is built
 * without VITE_ENABLE_MOCK, so this is constant-false there and the mock
 * module is tree-shaken out entirely.
 */
export function isMockMode(): boolean {
  if (!MOCK_BUILD) return false
  if (runtimeConfig().mock) return true
  try {
    const param = new URLSearchParams(window.location.search).get('mock')
    if (param === '1') sessionStorage.setItem(MOCK_SESSION_KEY, '1')
    if (param === '0') sessionStorage.removeItem(MOCK_SESSION_KEY)
    return sessionStorage.getItem(MOCK_SESSION_KEY) === '1'
  } catch {
    return false
  }
}
